import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { 
  BlogPost,
  createBlogPost,
  updateBlogPost,
  generateSlug,
  BLOG_CATEGORIES,
  BLOG_LANGUAGES,
} from '@/lib/blog-service';
import { Loader2, Save, Eye, FileText } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface BlogPostFormDialogProps {
  post?: BlogPost | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPostSaved: () => void;
}

const BlogPostFormDialog = ({
  post,
  open,
  onOpenChange,
  onPostSaved,
}: BlogPostFormDialogProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    featured_image: '',
    category: '',
    tags: '',
    language: 'en',
    status: 'draft' as 'draft' | 'published',
    published_at: '',
  });

  const isEditing = !!post;

  // Reset form when dialog opens/closes or post changes
  useEffect(() => {
    if (open) {
      if (post) {
        setFormData({
          title: post.title,
          slug: post.slug,
          excerpt: post.excerpt || '',
          content: post.content,
          featured_image: post.featured_image || '',
          category: post.category || '',
          tags: post.tags?.join(', ') || '',
          language: post.language,
          status: post.status,
          published_at: post.published_at ? format(new Date(post.published_at), "yyyy-MM-dd'T'HH:mm") : '',
        });
      } else {
        setFormData({
          title: '',
          slug: '',
          excerpt: '',
          content: '',
          featured_image: '',
          category: '',
          tags: '',
          language: 'en',
          status: 'draft',
          published_at: '',
        });
      }
    }
  }, [open, post]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };
      
      // Auto-generate slug from title if slug is empty or matches auto-generated pattern
      if (field === 'title' && (!prev.slug || prev.slug === generateSlug(prev.title))) {
        newData.slug = generateSlug(value);
      }
      
      return newData;
    });
  };

  const handleSubmit = async (saveStatus: 'draft' | 'published') => {
    // Validation
    if (!formData.title.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Title is required',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.content.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Content is required',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.slug.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Slug is required',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const postData = {
        title: formData.title.trim(),
        slug: formData.slug.trim(),
        excerpt: formData.excerpt.trim() || undefined,
        content: formData.content,
        featured_image: formData.featured_image.trim() || undefined,
        category: formData.category || undefined,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : undefined,
        language: formData.language,
        status: saveStatus,
        published_at: saveStatus === 'published' && formData.published_at 
          ? new Date(formData.published_at).toISOString() 
          : undefined,
      };

      if (isEditing && post) {
        await updateBlogPost(post.id, postData);
        toast({
          title: 'Post Updated',
          description: `"${formData.title}" has been updated`,
        });
      } else {
        await createBlogPost(postData);
        toast({
          title: 'Post Created',
          description: `"${formData.title}" has been created as ${saveStatus}`,
        });
      }

      onOpenChange(false);
      onPostSaved();
    } catch (error: any) {
      console.error('Error saving post:', error);
      
      // Check for unique constraint violation on slug
      if (error.code === '23505') {
        toast({
          title: 'Error',
          description: 'A post with this slug already exists. Please use a different slug.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Error',
          description: error.message || 'Failed to save post',
          variant: 'destructive',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {isEditing ? 'Edit Post' : 'Create New Post'}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? 'Update the blog post details below' : 'Fill in the details for the new blog post'}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="content" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="content">Content</TabsTrigger>
            <TabsTrigger value="meta">Meta & SEO</TabsTrigger>
            <TabsTrigger value="preview">Preview</TabsTrigger>
          </TabsList>

          <TabsContent value="content" className="space-y-4 mt-4">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Enter post title"
              />
            </div>

            {/* Slug */}
            <div className="space-y-2">
              <Label htmlFor="slug">Slug *</Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => handleInputChange('slug', e.target.value)}
                placeholder="post-url-slug"
              />
              <p className="text-xs text-muted-foreground">
                URL: /blog/{formData.slug || 'post-slug'}
              </p>
            </div>

            {/* Excerpt */}
            <div className="space-y-2">
              <Label htmlFor="excerpt">Excerpt / Summary</Label>
              <Textarea
                id="excerpt"
                value={formData.excerpt}
                onChange={(e) => handleInputChange('excerpt', e.target.value)}
                placeholder="Brief summary of the post (shown in listings)"
                rows={2}
              />
            </div>

            {/* Content */}
            <div className="space-y-2">
              <Label htmlFor="content">Content *</Label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={(e) => handleInputChange('content', e.target.value)}
                placeholder="Write your post content here. You can use Markdown formatting."
                rows={15}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Supports Markdown: **bold**, *italic*, ## headings, [links](url), etc.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="meta" className="space-y-4 mt-4">
            {/* Featured Image */}
            <div className="space-y-2">
              <Label htmlFor="featured_image">Featured Image URL</Label>
              <Input
                id="featured_image"
                value={formData.featured_image}
                onChange={(e) => handleInputChange('featured_image', e.target.value)}
                placeholder="https://example.com/image.jpg"
              />
              {formData.featured_image && (
                <div className="mt-2">
                  <img 
                    src={formData.featured_image} 
                    alt="Preview" 
                    className="max-h-32 rounded-md object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select value={formData.category} onValueChange={(value) => handleInputChange('category', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {BLOG_CATEGORIES.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Language */}
              <div className="space-y-2">
                <Label htmlFor="language">Language *</Label>
                <Select value={formData.language} onValueChange={(value) => handleInputChange('language', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BLOG_LANGUAGES.map((lang) => (
                      <SelectItem key={lang.value} value={lang.value}>
                        {lang.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <Label htmlFor="tags">Tags</Label>
              <Input
                id="tags"
                value={formData.tags}
                onChange={(e) => handleInputChange('tags', e.target.value)}
                placeholder="immigration, visa, green card (comma separated)"
              />
            </div>

            {/* Published At */}
            <div className="space-y-2">
              <Label htmlFor="published_at">Publish Date</Label>
              <Input
                id="published_at"
                type="datetime-local"
                value={formData.published_at}
                onChange={(e) => handleInputChange('published_at', e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to use current date when publishing
              </p>
            </div>
          </TabsContent>

          <TabsContent value="preview" className="mt-4">
            <div className="border rounded-lg p-6 bg-background">
              {formData.featured_image && (
                <img 
                  src={formData.featured_image} 
                  alt={formData.title} 
                  className="w-full h-48 object-cover rounded-lg mb-4"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              )}
              <h1 className="text-3xl font-bold mb-2">{formData.title || 'Post Title'}</h1>
              {formData.category && (
                <span className="inline-block bg-primary/10 text-primary px-2 py-1 rounded text-sm mb-4">
                  {formData.category}
                </span>
              )}
              {formData.excerpt && (
                <p className="text-muted-foreground mb-4 italic">{formData.excerpt}</p>
              )}
              <div className="prose prose-sm max-w-none">
                <div className="whitespace-pre-wrap">{formData.content || 'Post content will appear here...'}</div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex flex-col sm:flex-row gap-2 mt-6">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => handleSubmit('draft')}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save as Draft
          </Button>
          <Button
            type="button"
            onClick={() => handleSubmit('published')}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Eye className="h-4 w-4 mr-2" />
            )}
            {isEditing ? 'Update & Publish' : 'Publish'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BlogPostFormDialog;
